import { forcaPorHabilidade, modoHabilidadeLigado } from './habilidades';
import { LISTA_PAPEIS, MAX_AJUSTES, MAX_PAPEIS, MAX_PLANO_SUBS } from './tipos';
import type { AjusteIntervalo, Clube, CondicaoJogo, ConcentracaoAtaque, Escalacao, Estilo, Formacao, Jogador, Marcacao, Papel, Passe, PlanoSub, Posicao, Pressao, Tatica } from './tipos';

/** Quantidade de jogadores por posição em cada formação (G=1; soma 11). */
export const FORMACOES: Record<Formacao, Record<Posicao, number>> = {
  '4-4-2': { G: 1, Z: 2, L: 2, M: 4, A: 2 },
  '4-3-3': { G: 1, Z: 2, L: 2, M: 3, A: 3 },
  '4-5-1': { G: 1, Z: 2, L: 2, M: 5, A: 1 },
  '3-5-2': { G: 1, Z: 3, L: 0, M: 5, A: 2 },
  '5-3-2': { G: 1, Z: 3, L: 2, M: 3, A: 2 },
  '3-4-3': { G: 1, Z: 3, L: 0, M: 4, A: 3 },
  '5-4-1': { G: 1, Z: 3, L: 2, M: 4, A: 1 },
  // as posições do jogo são só G/Z/L/M/A: volantes e meias de ligação contam como M; o segundo atacante e os pontas, como A ou M conforme o desenho
  '4-2-3-1': { G: 1, Z: 2, L: 2, M: 5, A: 1 },
  '4-1-4-1': { G: 1, Z: 2, L: 2, M: 5, A: 1 },
  '4-3-1-2': { G: 1, Z: 2, L: 2, M: 4, A: 2 },
  '4-4-1-1': { G: 1, Z: 2, L: 2, M: 5, A: 1 },
  '4-2-4': { G: 1, Z: 2, L: 2, M: 2, A: 4 },
  '4-3-2-1': { G: 1, Z: 2, L: 2, M: 5, A: 1 },
  '3-4-2-1': { G: 1, Z: 3, L: 0, M: 6, A: 1 },
  '3-4-1-2': { G: 1, Z: 3, L: 0, M: 5, A: 2 },
  '3-6-1': { G: 1, Z: 3, L: 0, M: 6, A: 1 },
  '5-2-3': { G: 1, Z: 3, L: 2, M: 2, A: 3 },
};

export const LISTA_FORMACOES = Object.keys(FORMACOES) as Formacao[];

/** Formações que a escalação automática e a CPU escolhem sozinhas (as novas são só para o técnico montar à mão). */
export const FORMACOES_AUTO: readonly Formacao[] = ['4-4-2', '4-3-3', '4-5-1', '3-5-2', '5-3-2', '3-4-3', '5-4-1'];

export const TATICA_PADRAO = (): Tatica => ({ formacao: 'auto', estilo: 'equilibrado', marcacao: 'leve', ataque: 'meio', titulares: null });

const ORDEM_SLOTS: Posicao[] = ['G', 'Z', 'L', 'M', 'A'];

/** Slots da formação, na ordem G, Z, L, M, A. O índice do slot é o índice em `titulares`. */
export function slotsDaFormacao(f: Formacao): Posicao[] {
  const slots: Posicao[] = [];
  for (const pos of ORDEM_SLOTS) for (let i = 0; i < FORMACOES[f][pos]; i++) slots.push(pos);
  return slots;
}

const PROXIMIDADE: Record<Posicao, Record<Posicao, number>> = {
  G: { G: 1, Z: 0.3, L: 0.3, M: 0.25, A: 0.25 },
  Z: { G: 0.2, Z: 1, L: 0.8, M: 0.65, A: 0.5 },
  L: { G: 0.2, Z: 0.8, L: 1, M: 0.8, A: 0.65 },
  M: { G: 0.2, Z: 0.65, L: 0.8, M: 1, A: 0.8 },
  A: { G: 0.2, Z: 0.5, L: 0.65, M: 0.8, A: 1 },
};

/** Multiplicador de força de um jogador escalado numa posição diferente da natural. */
export function fatorPosicao(natural: Posicao, slot: Posicao): number {
  return PROXIMIDADE[natural][slot];
}

export function disponivel(j: Jogador): boolean {
  return j.lesaoRodadas <= 0 && j.suspensaoRodadas <= 0;
}

/** Força de 0 a 10 de um jogador com a energia dada (a energia tira até 25%). */
export function forcaComEnergia(j: Jogador, energia: number): number {
  // modo "habilidade individual": a força vem da média ponderada das habilidades da posição dele
  const base = modoHabilidadeLigado() ? forcaPorHabilidade(j) : j.overall;
  return (base * (0.75 + 0.25 * (energia / 100))) / 10;
}

/** Força de 0 a 10 de um jogador descansado ou não. */
export function forcaBase(j: Jogador): number {
  return forcaComEnergia(j, j.energia);
}

/**
 * Monta os onze por slot. Primeiro cada posição recebe os melhores jogadores naturais dela; os slots que sobrarem
 * (falta de gente numa posição) são preenchidos pelo melhor disponível, descontada a posição (fatorPosicao).
 */
export function escalarAutoFormacao(clube: Clube, formacao: Formacao, estilo: Tatica['estilo'] = 'equilibrado'): Escalacao {
  const livres = clube.elenco.filter(disponivel);
  const usados = new Set<number>();
  const slots = slotsDaFormacao(formacao);
  const porSlot: (Jogador | undefined)[] = slots.map(() => undefined);

  for (const pos of ORDEM_SLOTS) {
    const naturais = livres.filter((j) => j.posicao === pos).sort((a, b) => forcaBase(b) - forcaBase(a));
    let k = 0;
    slots.forEach((s, i) => {
      const j = naturais[k];
      if (s === pos && j) {
        porSlot[i] = j;
        usados.add(j.id);
        k++;
      }
    });
  }
  slots.forEach((slot, i) => {
    if (porSlot[i]) return;
    let melhor: Jogador | undefined;
    let melhorNota = -1;
    for (const j of livres) {
      if (usados.has(j.id)) continue;
      const nota = forcaBase(j) * fatorPosicao(j.posicao, slot);
      if (nota > melhorNota) {
        melhorNota = nota;
        melhor = j;
      }
    }
    if (melhor) {
      usados.add(melhor.id);
      porSlot[i] = melhor;
    }
  });

  const titulares: Jogador[] = [];
  const slotsUsados: Posicao[] = [];
  porSlot.forEach((j, i) => {
    if (j) {
      titulares.push(j);
      slotsUsados.push(slots[i] as Posicao);
    }
  });
  return { titulares, reservas: escolherReservas(livres, usados), slots: slotsUsados, estilo, marcacao: clube.tatica.marcacao, ataque: clube.tatica.ataque, passe: clube.tatica.passe ?? 'misto', pressao: clube.tatica.pressao ?? 'media' };
}

/** Jogadores no banco de reservas (as substituições por jogo continuam limitadas a MAX_SUBSTITUICOES). */
export const BANCO_MAX = 12;

function escolherReservas(livres: Jogador[], usados: Set<number>): Jogador[] {
  const reservas: Jogador[] = [];
  const goleiro = livres.filter((j) => !usados.has(j.id) && j.posicao === 'G').sort((a, b) => forcaBase(b) - forcaBase(a))[0];
  if (goleiro) reservas.push(goleiro);
  const outros = livres
    .filter((j) => !usados.has(j.id) && j !== goleiro)
    .sort((a, b) => forcaBase(b) - forcaBase(a))
    .slice(0, BANCO_MAX - reservas.length);
  reservas.push(...outros);
  return reservas;
}

/**
 * Quantos jogadores cada linha "conta" no motor. A força da linha é a soma dos melhores N dividida por N fixo,
 * então formações com menos gente numa linha rendem menos nela. O ataque usa 4 (e não 3, como no original)
 * para que formações com 3 atacantes não dominem as demais (ver scripts/calibrar.ts).
 */
export const LINHAS = { meio: 5, ataque: 4, defesa: 5 } as const;

/** Nota que imita o motor: goleiro + defesa + meio + ataque, cada linha com seu divisor fixo. */
function notaEscalacao(esc: Escalacao): number {
  const slots = esc.slots ?? [];
  const linha = (posicoes: Posicao[], n: number): number => {
    const v = esc.titulares
      .map((j, i) => ({ pos: slots[i] ?? j.posicao, f: forcaBase(j) * fatorPosicao(j.posicao, slots[i] ?? j.posicao) }))
      .filter((x) => posicoes.includes(x.pos))
      .map((x) => x.f)
      .sort((a, b) => b - a)
      .slice(0, n);
    return v.length ? v.reduce((s, x) => s + x, 0) / n : 0.1;
  };
  return linha(['G'], 1) + linha(['Z', 'L'], LINHAS.defesa) + linha(['M'], LINHAS.meio) + linha(['A'], LINHAS.ataque);
}

/** Formação que rende mais com o elenco disponível (empate: a primeira da lista). */
export function melhorFormacao(clube: Clube): Formacao {
  let melhor: Formacao = '4-4-2';
  let nota = -1;
  for (const f of FORMACOES_AUTO) {
    const n = notaEscalacao(escalarAutoFormacao(clube, f));
    if (n > nota + 1e-9) {
      nota = n;
      melhor = f;
    }
  }
  return melhor;
}

/** Aplica o banco manual (se houver e ainda for válido) sobre uma escalação já montada. */
function aplicarBancoManual(clube: Clube, esc: Escalacao): Escalacao {
  const ids = clube.tatica.reservas;
  if (!ids || ids.length === 0) return esc;
  const emCampo = new Set(esc.titulares.map((j) => j.id));
  const banco = ids
    .map((id) => clube.elenco.find((j) => j.id === id))
    .filter((j): j is Jogador => Boolean(j) && disponivel(j as Jogador) && !emCampo.has((j as Jogador).id))
    .slice(0, BANCO_MAX);
  return banco.length > 0 ? { ...esc, reservas: banco } : esc;
}

export interface EscalacaoResolvida {
  escalacao: Escalacao;
  formacao: Formacao;
  avisos: string[];
}

/** Valida uma escalação manual; devolve a lista de problemas (vazia se estiver tudo certo). */
export function validarTitulares(clube: Clube, formacao: Formacao, ids: number[]): string[] {
  const erros: string[] = [];
  const slots = slotsDaFormacao(formacao);
  if (ids.length !== slots.length) erros.push(`são necessários ${slots.length} titulares`);
  if (new Set(ids).size !== ids.length) erros.push('há jogador repetido');
  ids.forEach((id, i) => {
    const j = clube.elenco.find((x) => x.id === id);
    if (!j) erros.push(`jogador ${id} não pertence ao clube`);
    else if (!disponivel(j)) erros.push(`${j.nome} está indisponível`);
    else if (slots[i] === 'G' && j.posicao !== 'G') erros.push(`${j.nome} não é goleiro`);
  });
  return erros;
}

const CONDICOES: readonly CondicaoJogo[] = ['sempre', 'perdendo', 'empatando', 'ganhando'];
const ESTILOS: readonly Estilo[] = ['retranca', 'defensivo', 'equilibrado', 'ofensivo', 'total'];
const MARCACOES: readonly Marcacao[] = ['leve', 'normal', 'forte'];
const ATAQUES: readonly ConcentracaoAtaque[] = ['meio', 'lados', 'equilibrado'];
const PASSES: readonly Passe[] = ['curto', 'misto', 'longo'];
const PRESSOES: readonly Pressao[] = ['baixa', 'media', 'alta'];

export type PlanoDeJogo = Pick<Tatica, 'substituicoes' | 'ajustes' | 'capitaoId' | 'batedorPenaltiId' | 'batedorFaltaId' | 'batedorEscanteioId' | 'falso9' | 'papeis'>;

/**
 * Valida e normaliza o plano de substituições, os ajustes do intervalo e as funções especiais vindos de fora
 * (cliente solo ou comando do servidor). Lança Error com a mensagem do primeiro problema.
 */
export function normalizarPlano(clube: Clube, t: Partial<PlanoDeJogo>): PlanoDeJogo {
  const ids = new Set(clube.elenco.map((j) => j.id));
  const idOuNulo = (v: unknown, nome: string): number | null => {
    if (v === null || v === undefined) return null;
    if (!Number.isInteger(v) || !ids.has(v as number)) throw new Error(`${nome}: jogador inválido`);
    return v as number;
  };

  const subs: PlanoSub[] = [];
  if (t.substituicoes !== undefined && t.substituicoes !== null) {
    if (!Array.isArray(t.substituicoes) || t.substituicoes.length > MAX_PLANO_SUBS) throw new Error(`no máximo ${MAX_PLANO_SUBS} substituições planejadas`);
    const saem = new Set<number>();
    const entram = new Set<number>();
    for (const s of t.substituicoes as PlanoSub[]) {
      if (typeof s !== 'object' || s === null) throw new Error('substituição inválida');
      const saiId = idOuNulo(s.saiId, 'substituição');
      const entraId = idOuNulo(s.entraId, 'substituição');
      if (saiId === null || entraId === null || saiId === entraId) throw new Error('substituição inválida');
      if (!Number.isInteger(s.minuto) || s.minuto < 0 || s.minuto > 90) throw new Error('o minuto da substituição deve estar entre 0 (intervalo) e 90');
      if (!CONDICOES.includes(s.condicao)) throw new Error('condição da substituição inválida');
      if (saem.has(saiId) || entram.has(entraId)) throw new Error('o mesmo jogador aparece duas vezes no plano');
      saem.add(saiId);
      entram.add(entraId);
      subs.push({ saiId, entraId, minuto: s.minuto, condicao: s.condicao });
    }
  }

  const ajustes: AjusteIntervalo[] = [];
  if (t.ajustes !== undefined && t.ajustes !== null) {
    if (!Array.isArray(t.ajustes) || t.ajustes.length > MAX_AJUSTES) throw new Error(`no máximo ${MAX_AJUSTES} ajustes de intervalo`);
    for (const a of t.ajustes as AjusteIntervalo[]) {
      if (typeof a !== 'object' || a === null || !CONDICOES.includes(a.condicao)) throw new Error('ajuste de intervalo inválido');
      const aj: AjusteIntervalo = { condicao: a.condicao };
      if (a.estilo !== undefined) {
        if (!ESTILOS.includes(a.estilo)) throw new Error('estilo inválido');
        aj.estilo = a.estilo;
      }
      if (a.marcacao !== undefined) {
        if (!MARCACOES.includes(a.marcacao)) throw new Error('marcação inválida');
        aj.marcacao = a.marcacao;
      }
      if (a.ataque !== undefined) {
        if (!ATAQUES.includes(a.ataque)) throw new Error('concentração de ataque inválida');
        aj.ataque = a.ataque;
      }
      if (a.passe !== undefined) {
        if (!PASSES.includes(a.passe)) throw new Error('estilo de passe inválido');
        aj.passe = a.passe;
      }
      if (a.pressao !== undefined) {
        if (!PRESSOES.includes(a.pressao)) throw new Error('pressão inválida');
        aj.pressao = a.pressao;
      }
      if (aj.estilo || aj.marcacao || aj.ataque || aj.passe || aj.pressao) ajustes.push(aj);
    }
  }

  const papeis: Record<number, Papel> = {};
  if (t.papeis !== undefined && t.papeis !== null) {
    if (typeof t.papeis !== 'object' || Array.isArray(t.papeis)) throw new Error('funções individuais inválidas');
    const entradas = Object.entries(t.papeis);
    if (entradas.length > MAX_PAPEIS) throw new Error(`no máximo ${MAX_PAPEIS} funções individuais`);
    for (const [k, v] of entradas) {
      const id = Number(k);
      if (!Number.isInteger(id) || !ids.has(id)) throw new Error('função individual: jogador inválido');
      if (!(LISTA_PAPEIS as readonly string[]).includes(v as string)) throw new Error('função individual inválida');
      papeis[id] = v as Papel;
    }
  }

  return {
    substituicoes: subs,
    ajustes,
    papeis,
    capitaoId: idOuNulo(t.capitaoId, 'capitão'),
    batedorPenaltiId: idOuNulo(t.batedorPenaltiId, 'batedor de pênaltis'),
    batedorFaltaId: idOuNulo(t.batedorFaltaId, 'batedor de faltas'),
    batedorEscanteioId: idOuNulo(t.batedorEscanteioId, 'batedor de escanteios'),
    falso9: Boolean(t.falso9),
  };
}

/**
 * Resolve a escalação efetiva do clube a partir da tática, trocando indisponíveis por substitutos,
 * e anexa o plano de substituições, os ajustes do intervalo e as funções especiais (capitão, batedores, Falso 9).
 */
export function resolverEscalacao(clube: Clube): EscalacaoResolvida {
  const r = resolverBase(clube);
  const t = clube.tatica;
  const esc = r.escalacao;
  if (t.substituicoes?.length) esc.plano = t.substituicoes;
  if (t.ajustes?.length) esc.ajustes = t.ajustes;
  esc.funcoes = {
    capitaoId: t.capitaoId ?? null,
    batedorPenaltiId: t.batedorPenaltiId ?? null,
    batedorFaltaId: t.batedorFaltaId ?? null,
    batedorEscanteioId: t.batedorEscanteioId ?? null,
    falso9: Boolean(t.falso9),
    papeis: t.papeis ?? {},
  };
  esc.passe = t.passe ?? 'misto';
  esc.pressao = t.pressao ?? 'media';
  return r;
}

function resolverBase(clube: Clube): EscalacaoResolvida {
  const t = clube.tatica;
  const formacao = t.formacao === 'auto' ? melhorFormacao(clube) : t.formacao;
  const avisos: string[] = [];
  const slots = slotsDaFormacao(formacao);

  if (!t.titulares || t.titulares.length !== slots.length) {
    return { escalacao: aplicarBancoManual(clube, escalarAutoFormacao(clube, formacao, t.estilo)), formacao, avisos };
  }

  const usados = new Set<number>();
  const titulares: Jogador[] = [];
  const pendentes: number[] = [];
  t.titulares.forEach((id, i) => {
    const j = clube.elenco.find((x) => x.id === id);
    if (j && disponivel(j) && !usados.has(j.id) && (slots[i] !== 'G' || j.posicao === 'G')) {
      usados.add(j.id);
      titulares[i] = j;
    } else {
      pendentes.push(i);
      if (j && !disponivel(j)) avisos.push(`${j.nome} está indisponível e foi substituído.`);
      else if (!j) avisos.push('um titular saiu do clube e foi substituído.');
    }
  });
  const livres = clube.elenco.filter((j) => disponivel(j) && !usados.has(j.id));
  for (const i of pendentes) {
    const slot = slots[i] as Posicao;
    let melhor: Jogador | undefined;
    let melhorNota = -1;
    for (const j of livres) {
      if (usados.has(j.id)) continue;
      if (slot === 'G' && j.posicao !== 'G') continue;
      const nota = forcaBase(j) * fatorPosicao(j.posicao, slot);
      if (nota > melhorNota) {
        melhorNota = nota;
        melhor = j;
      }
    }
    if (melhor) {
      usados.add(melhor.id);
      titulares[i] = melhor;
    }
  }
  const completos = titulares.filter((j): j is Jogador => Boolean(j));
  if (completos.length !== slots.length) {
    // elenco insuficiente: volta para a escalação automática
    avisos.push('elenco insuficiente para a escalação manual; usando a automática.');
    return { escalacao: escalarAutoFormacao(clube, formacao, t.estilo), formacao, avisos };
  }
  const manual: Escalacao = {
    titulares: completos,
    reservas: escolherReservas(clube.elenco.filter(disponivel), usados),
    slots,
    estilo: t.estilo,
    marcacao: t.marcacao,
    ataque: t.ataque,
  };
  return { escalacao: aplicarBancoManual(clube, manual), formacao, avisos };
}
